/*
 * C Blog Listing (sections/c-blog-listing.liquid)
 *
 * - Client-side category filter (OR across ticked categories), newest first, paginated.
 * - State in the URL: ?category=a,b&p=2. Back / Forward restore it.
 * - Pills, desktop checkboxes and the mobile modal share one state.
 * - Pills that do not fit move into "More".
 * - Predictive search, articles only.
 * - Featured slider dots (mobile / tablet).
 */
(function () {
  'use strict';

  var CATEGORY_ORDER = [
    ['Category_In the Press', 'in-the-press', 'In the Press'],
    ['Category_Barber Talk', 'barber-talk', 'Barber Talk'],
    ['Category_How-To', 'how-to', 'How-To'],
    ['Category_Gift Guides', 'gift-guides', 'Gift Guides'],
    ['Category_News', 'news', 'News'],
  ];
  var HANDLES = CATEGORY_ORDER.map(function (c) {
    return c[1];
  });
  var LABELS = {};
  CATEGORY_ORDER.forEach(function (c) {
    LABELS[c[1]] = c[2];
  });

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function sortHandles(list) {
    return HANDLES.filter(function (h) {
      return list.indexOf(h) !== -1;
    });
  }

  function BlogListing(root) {
    this.root = root;
    this.perPage = parseInt(root.dataset.perPage, 10) || 24;
    this.wpm = parseInt(root.dataset.wpm, 10) || 200;
    this.blogUrl = root.dataset.blogUrl || '/blogs/blog';
    this.tagged = root.dataset.tagged ? root.dataset.tagged.split(',') : [];

    this.grid = root.querySelector('[data-c-blog-list-grid]');
    this.results = root.querySelector('[data-c-blog-list-results]');
    this.empty = root.querySelector('[data-c-blog-list-empty]');
    this.pagination = root.querySelector('[data-c-blog-list-pagination]');

    this.items = this.grid ? qsa(this.grid, '[data-c-blog-list-item]') : [];
    this.items.forEach(function (item) {
      item._cats = item.dataset.categories ? item.dataset.categories.split(',') : [];
      item._date = parseInt(item.dataset.date, 10) || 0;
    });
    // Newest first. Stable sort keeps Shopify's order for equal dates.
    this.items = this.items
      .map(function (item, i) {
        return { item: item, i: i };
      })
      .sort(function (a, b) {
        return b.item._date - a.item._date || a.i - b.i;
      })
      .map(function (x) {
        return x.item;
      });
    var grid = this.grid;
    this.items.forEach(function (item) {
      grid.appendChild(item);
    });

    this.state = this.readUrl();

    this.initPills();
    this.initCheckboxes();
    this.initModal();
    this.initSearch();
    this.initBand();

    var self = this;
    window.addEventListener('popstate', function () {
      self.state = self.readUrl();
      self.render();
    });

    this.render();
    root.classList.add('c-blog-list--ready');
  }

  // Icons are rendered by Liquid snippets into <template data-c-blog-list-icon> elements.
  BlogListing.prototype.icon = function (name) {
    var tpl = this.root.querySelector('template[data-c-blog-list-icon="' + name + '"]');
    return tpl ? tpl.innerHTML.trim() : '';
  };

  /* ---------- State + URL ---------- */

  BlogListing.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var cats = (params.get('category') || '')
      .split(',')
      .map(function (s) {
        return s.trim().toLowerCase();
      })
      .filter(function (s) {
        return HANDLES.indexOf(s) !== -1;
      });
    if (!params.has('category') && this.tagged.length) cats = this.tagged.slice();
    var page = parseInt(params.get('p'), 10);
    return { cats: sortHandles(cats), page: page > 0 ? page : 1 };
  };

  BlogListing.prototype.buildUrl = function (cats, page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('category');
    params.delete('p');
    params.delete('page');
    // On a /tagged/ page showing exactly its own tag, stay there; anything else is the full listing.
    var onTagged = this.tagged.length && cats.join(',') === this.tagged.join(',');
    if (cats.length && !onTagged) params.set('category', cats.join(','));
    if (page > 1) params.set('p', String(page));
    var qs = params.toString().replace(/%2C/gi, ',');
    var base = this.tagged.length && !onTagged ? this.blogUrl : window.location.pathname;
    return base + (qs ? '?' + qs : '');
  };

  BlogListing.prototype.setState = function (cats, page, opts) {
    cats = sortHandles(cats);
    page = page || 1;
    opts = opts || {};
    // A /tagged/ page only holds that tag's articles: a new filter needs the full listing.
    if (this.tagged.length && cats.join(',') !== this.tagged.join(',')) {
      window.location.href = this.buildUrl(cats, page);
      return;
    }
    this.state = { cats: cats, page: page };
    var url = this.buildUrl(cats, page);
    if (url !== window.location.pathname + window.location.search) {
      window.history.pushState({ cbl: true }, '', url);
    }
    this.render();
    if (opts.scroll) this.scrollToResults();
  };

  BlogListing.prototype.scrollToResults = function () {
    var target = this.root.querySelector('.c-blog-list-toolbar') || this.results;
    var headerH = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
    if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
  };

  BlogListing.prototype.filtered = function (cats) {
    if (!cats.length) return this.items.slice();
    return this.items.filter(function (item) {
      return item._cats.some(function (c) {
        return cats.indexOf(c) !== -1;
      });
    });
  };

  /* ---------- Render ---------- */

  BlogListing.prototype.render = function () {
    var cats = this.state.cats;
    var list = this.filtered(cats);
    var pages = Math.max(1, Math.ceil(list.length / this.perPage));
    var page = Math.min(this.state.page, pages);
    this.state.page = page;
    var start = (page - 1) * this.perPage;
    var visible = list.slice(start, start + this.perPage);

    this.items.forEach(function (item) {
      item.hidden = true;
    });
    visible.forEach(function (item, i) {
      item.hidden = false;
      var img = item.querySelector('img');
      if (img && i < 3) img.loading = 'eager';
    });

    if (this.empty) this.empty.hidden = list.length > 0;
    this.renderPagination(page, pages);
    this.syncControls();
  };

  BlogListing.prototype.renderPagination = function (page, pages) {
    var nav = this.pagination;
    if (!nav) return;
    if (pages <= 1) {
      nav.hidden = true;
      nav.innerHTML = '';
      return;
    }
    var self = this;
    var cats = this.state.cats;
    var arrowLeft = this.icon('prev');
    var arrowRight = this.icon('next');

    function arrow(target, label, svg) {
      if (target < 1 || target > pages) {
        return (
          '<li><span class="c-blog-list-pagination__link c-blog-list-pagination__link--arrow c-blog-list-pagination__link--disabled" aria-disabled="true" aria-label="' +
          label +
          '">' +
          svg +
          '</span></li>'
        );
      }
      return (
        '<li><a class="c-blog-list-pagination__link c-blog-list-pagination__link--arrow" href="' +
        escapeHtml(self.buildUrl(cats, target)) +
        '" data-page="' +
        target +
        '" aria-label="' +
        label +
        '">' +
        svg +
        '</a></li>'
      );
    }

    // 1 … (page-1) page (page+1) … last
    var nums = [];
    for (var n = 1; n <= pages; n++) {
      if (n === 1 || n === pages || Math.abs(n - page) <= 1) nums.push(n);
      else if (nums[nums.length - 1] !== '…') nums.push('…');
    }

    var html = '<ul class="c-blog-list-pagination__list" role="list">';
    html += arrow(page - 1, 'Previous page', arrowLeft);
    nums.forEach(function (n) {
      if (n === '…') {
        html += '<li class="c-blog-list-pagination__gap" aria-hidden="true">…</li>';
      } else if (n === page) {
        html += '<li><span class="c-blog-list-pagination__link" aria-current="page">' + n + '</span></li>';
      } else {
        html +=
          '<li><a class="c-blog-list-pagination__link" href="' +
          escapeHtml(self.buildUrl(cats, n)) +
          '" data-page="' +
          n +
          '" aria-label="Page ' +
          n +
          '">' +
          n +
          '</a></li>';
      }
    });
    html += arrow(page + 1, 'Next page', arrowRight);
    html += '</ul>';
    nav.innerHTML = html;
    nav.hidden = false;

    qsa(nav, 'a[data-page]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        self.setState(self.state.cats, parseInt(a.dataset.page, 10), { scroll: true });
      });
    });
  };

  BlogListing.prototype.syncControls = function () {
    var cats = this.state.cats;
    var single = cats.length === 1 ? cats[0] : null;

    // Pills
    qsa(this.root, '.c-blog-list-pill[data-c-blog-list-pill], .c-blog-list-more__link[data-c-blog-list-pill]').forEach(function (pill) {
      var h = pill.dataset.cBlogListPill;
      var active = h === '' ? cats.length === 0 : h === single;
      if (active) pill.setAttribute('aria-current', 'page');
      else pill.removeAttribute('aria-current');
    });
    this.updateMoreLabel();

    // Desktop checkboxes
    qsa(this.root, '[data-c-blog-list-check] input[type="checkbox"]').forEach(function (input) {
      input.checked = cats.indexOf(input.value) !== -1;
    });

    // Filter button bubble
    var bubble = this.root.querySelector('[data-c-blog-list-bubble]');
    var bubbleText = this.root.querySelector('[data-c-blog-list-bubble-text]');
    var toggle = this.root.querySelector('[data-c-blog-list-open]');
    if (bubble && bubbleText) {
      bubble.hidden = cats.length === 0;
      bubbleText.textContent = cats.length;
    }
    if (toggle) {
      toggle.setAttribute(
        'aria-label',
        cats.length ? 'Filter articles, ' + cats.length + ' active' : 'Filter articles'
      );
    }
  };

  /* ---------- Pills + More ---------- */

  BlogListing.prototype.initPills = function () {
    var self = this;
    this.pillsNav = this.root.querySelector('[data-c-blog-list-pills]');
    if (!this.pillsNav) return;
    this.pillRow = this.pillsNav.querySelector('[data-c-blog-list-pill-row]');
    this.more = this.pillsNav.querySelector('[data-c-blog-list-more]');
    this.moreToggle = this.pillsNav.querySelector('[data-c-blog-list-more-toggle]');
    this.moreMenu = this.more ? this.more.querySelector('.c-blog-list-more__menu') : null;
    this.moreLabel = this.pillsNav.querySelector('[data-c-blog-list-more-label]');

    qsa(this.pillsNav, '[data-c-blog-list-pill]').forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault();
        var h = pill.dataset.cBlogListPill;
        self.closeMore();
        self.setState(h ? [h] : [], 1);
      });
    });

    if (this.moreToggle) {
      this.moreToggle.addEventListener('click', function () {
        if (self.moreMenu.hidden) self.openMore();
        else self.closeMore();
      });
      this.more.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.moreMenu.hidden) {
          e.stopPropagation();
          self.closeMore();
          self.moreToggle.focus();
        }
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !self.moreMenu.hidden) {
          var links = qsa(self.moreMenu, 'li:not([hidden]) a');
          if (!links.length) return;
          e.preventDefault();
          var i = links.indexOf(document.activeElement);
          i = e.key === 'ArrowDown' ? (i + 1) % links.length : (i - 1 + links.length) % links.length;
          links[i].focus();
        }
      });
      document.addEventListener('click', function (e) {
        if (self.more && !self.more.contains(e.target)) self.closeMore();
      });
    }

    this.fitPills();
    if ('ResizeObserver' in window) {
      var raf;
      new ResizeObserver(function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          self.fitPills();
        });
      }).observe(this.pillsNav);
    } else {
      window.addEventListener('resize', function () {
        self.fitPills();
      });
    }
  };

  BlogListing.prototype.openMore = function () {
    this.moreMenu.hidden = false;
    this.moreToggle.setAttribute('aria-expanded', 'true');
    var first = this.moreMenu.querySelector('li:not([hidden]) a');
    if (first) first.focus();
  };

  BlogListing.prototype.closeMore = function () {
    if (!this.moreMenu || this.moreMenu.hidden) return;
    this.moreMenu.hidden = true;
    this.moreToggle.setAttribute('aria-expanded', 'false');
  };

  // Move pills that do not fit into "More", from the end. Never wraps to a second row.
  BlogListing.prototype.fitPills = function () {
    if (!this.pillRow || !this.more) return;
    if (this.pillsNav.offsetParent === null) return; // hidden below 990px
    var pillItems = qsa(this.pillRow, '[data-c-blog-list-pill-item]');
    var menuItems = {};
    qsa(this.moreMenu, '[data-c-blog-list-more-item]').forEach(function (li) {
      menuItems[li.dataset.cBlogListMoreItem] = li;
    });

    pillItems.forEach(function (li) {
      li.hidden = false;
    });
    Object.keys(menuItems).forEach(function (k) {
      menuItems[k].hidden = true;
    });
    this.more.hidden = true;

    var avail = this.pillsNav.clientWidth;
    var self = this;
    function overflow() {
      return self.pillRow.scrollWidth > avail + 1;
    }
    if (overflow()) {
      this.more.hidden = false;
      for (var i = pillItems.length - 1; i >= 0 && overflow(); i--) {
        pillItems[i].hidden = true;
        menuItems[pillItems[i].dataset.cBlogListPillItem].hidden = false;
      }
    }
    this.updateMoreLabel();
  };

  BlogListing.prototype.updateMoreLabel = function () {
    if (!this.moreToggle || !this.moreLabel) return;
    var cats = this.state ? this.state.cats : [];
    var single = cats.length === 1 ? cats[0] : null;
    var inMore = null;
    if (single && this.moreMenu) {
      var li = this.moreMenu.querySelector('[data-c-blog-list-more-item="' + single + '"]');
      if (li && !li.hidden) inMore = single;
    }
    this.moreLabel.textContent = inMore ? LABELS[inMore] : 'More';
    this.moreToggle.classList.toggle('is-active', !!inMore);
  };

  /* ---------- Desktop checkboxes ---------- */

  BlogListing.prototype.initCheckboxes = function () {
    var self = this;
    var inputs = qsa(this.root, '[data-c-blog-list-check] input[type="checkbox"]');
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var cats = inputs
          .filter(function (i) {
            return i.checked;
          })
          .map(function (i) {
            return i.value;
          });
        self.setState(cats, 1);
      });
    });
    qsa(this.root, '[data-c-blog-list-clear]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        self.setState([], 1);
      });
    });
  };

  /* ---------- Mobile modal ---------- */

  BlogListing.prototype.initModal = function () {
    var self = this;
    var modal = this.root.querySelector('[data-c-blog-list-modal]');
    var openBtn = this.root.querySelector('[data-c-blog-list-open]');
    if (!modal || !openBtn) return;
    this.modal = modal;
    this.openBtn = openBtn;
    this.draft = [];
    this.modalInputs = qsa(modal, '[data-c-blog-list-modal-check] input[type="checkbox"]');
    this.chips = modal.querySelector('[data-c-blog-list-chips]');
    this.modalCount = modal.querySelector('[data-c-blog-list-modal-count]');
    this.modalApply = modal.querySelector('[data-c-blog-list-modal-apply]');
    this.modalClear = modal.querySelector('[data-c-blog-list-modal-clear]');

    openBtn.addEventListener('click', function () {
      self.openModal();
    });
    modal.querySelector('[data-c-blog-list-close]').addEventListener('click', function () {
      self.closeModal();
    });
    // Esc: discard, same as ×.
    modal.addEventListener('cancel', function (e) {
      e.preventDefault();
      self.closeModal();
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal) self.closeModal();
    });
    modal.addEventListener('close', function () {
      self.unlockScroll();
      openBtn.setAttribute('aria-expanded', 'false');
    });

    this.modalInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.draft = self.modalInputs
          .filter(function (i) {
            return i.checked;
          })
          .map(function (i) {
            return i.value;
          });
        self.renderModal();
      });
    });

    this.chips.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-c-blog-list-chip]');
      if (!btn) return;
      var h = btn.dataset.cBlogListChip;
      self.draft = self.draft.filter(function (c) {
        return c !== h;
      });
      self.renderModal();
    });

    this.modalApply.addEventListener('click', function () {
      var cats = self.draft.slice();
      self.closeModal();
      self.setState(cats, 1, { scroll: true });
    });

    this.modalClear.addEventListener('click', function () {
      self.closeModal();
      self.setState([], 1, { scroll: true });
    });

    window.matchMedia('(min-width: 990px)').addEventListener('change', function (mq) {
      if (mq.matches && modal.open) self.closeModal();
    });
  };

  BlogListing.prototype.openModal = function () {
    this.draft = this.state.cats.slice();
    this.renderModal();
    if (typeof this.modal.showModal === 'function') this.modal.showModal();
    else this.modal.setAttribute('open', '');
    this.lockScroll();
    this.openBtn.setAttribute('aria-expanded', 'true');
  };

  BlogListing.prototype.closeModal = function () {
    if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
    else this.modal.removeAttribute('open');
    this.unlockScroll();
    this.openBtn.setAttribute('aria-expanded', 'false');
    this.openBtn.focus();
  };

  BlogListing.prototype.lockScroll = function () {
    this._scrollY = window.pageYOffset;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  BlogListing.prototype.unlockScroll = function () {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  BlogListing.prototype.renderModal = function () {
    var draft = sortHandles(this.draft);
    var closeIcon = this.icon('close');
    this.draft = draft;
    this.modalInputs.forEach(function (input) {
      input.checked = draft.indexOf(input.value) !== -1;
    });

    this.chips.innerHTML = draft
      .map(function (h) {
        return (
          '<li><button type="button" class="c-blog-list-chip-btn" data-c-blog-list-chip="' +
          h +
          '" aria-label="Remove ' +
          escapeHtml(LABELS[h]) +
          '">' +
          escapeHtml(LABELS[h]) +
          closeIcon +
          '</button></li>'
        );
      })
      .join('');
    this.chips.hidden = draft.length === 0;

    this.modalCount.textContent = draft.length;
    this.modalCount.hidden = draft.length === 0;
    this.modalClear.hidden = draft.length === 0;

    var count = this.filtered(draft).length;
    this.modalApply.textContent = 'See ' + count + (count === 1 ? ' article' : ' articles');
  };

  /* ---------- Predictive search ---------- */

  BlogListing.prototype.initSearch = function () {
    var self = this;
    var form = this.root.querySelector('[data-c-blog-list-search]');
    if (!form) return;
    var input = form.querySelector('[data-c-blog-list-search-input]');
    var panel = form.querySelector('[data-c-blog-list-search-panel]');
    var list = form.querySelector('[data-c-blog-list-search-list]');
    var empty = form.querySelector('[data-c-blog-list-search-empty]');
    var all = form.querySelector('[data-c-blog-list-search-all]');
    var searchUrl = form.getAttribute('action') || '/search';
    var timer = null;
    var controller = null;
    var active = -1;
    var cache = {};

    function close() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }

    function options() {
      return qsa(list, '[role="option"]');
    }

    function setActive(i) {
      var opts = options();
      opts.forEach(function (o, idx) {
        o.setAttribute('aria-selected', idx === i ? 'true' : 'false');
      });
      active = i;
      if (i >= 0 && opts[i]) input.setAttribute('aria-activedescendant', opts[i].id);
      else input.removeAttribute('aria-activedescendant');
    }

    function categoryOf(tags) {
      tags = tags || [];
      for (var i = 0; i < CATEGORY_ORDER.length; i++) {
        if (tags.indexOf(CATEGORY_ORDER[i][0]) !== -1) return CATEGORY_ORDER[i][2];
      }
      return '';
    }

    function readTime(body) {
      var text = String(body || '')
        .replace(/<[^>]*>/g, ' ')
        .trim();
      var words = text ? text.split(/\s+/).length : 0;
      return Math.max(1, Math.ceil(words / self.wpm));
    }

    function thumb(url) {
      if (!url) return '';
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      return url + sep + 'width=96';
    }

    function show(q, articles) {
      var idBase = input.id + '-opt-';
      if (!articles.length) {
        list.innerHTML = '';
        empty.hidden = false;
        all.hidden = true;
      } else {
        empty.hidden = true;
        list.innerHTML = articles
          .map(function (a, i) {
            var cat = categoryOf(a.tags);
            var meta = (cat ? escapeHtml(cat) + ' · ' : '') + readTime(a.body) + ' min read';
            var img = a.image
              ? '<img src="' + escapeHtml(thumb(a.image)) + '" alt="" width="48" height="48" loading="lazy">'
              : '';
            return (
              '<li class="c-blog-list-search__option" role="option" id="' +
              idBase +
              i +
              '" aria-selected="false"><a href="' +
              escapeHtml(a.url) +
              '" tabindex="-1"><span class="c-blog-list-search__thumb">' +
              img +
              '</span><span><span class="c-blog-list-search__title">' +
              escapeHtml(a.title) +
              '</span><span class="c-blog-list-search__meta">' +
              meta +
              '</span></span></a></li>'
            );
          })
          .join('');
        all.hidden = false;
        all.href = searchUrl + '?q=' + encodeURIComponent(q) + '&type=article';
        all.textContent = 'View all results for "' + q + '"';
      }
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      setActive(-1);
    }

    function fetchResults(q) {
      if (cache[q]) {
        show(q, cache[q]);
        return;
      }
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var url =
        searchUrl +
        '/suggest.json?q=' +
        encodeURIComponent(q) +
        '&resources[type]=article&resources[limit]=3&resources[options][unavailable_products]=hide&resources[options][fields]=title,body';
      fetch(url, controller ? { signal: controller.signal } : {})
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var articles = (data && data.resources && data.resources.results && data.resources.results.articles) || [];
          cache[q] = articles;
          if (input.value.trim() === q) show(q, articles);
        })
        .catch(function () {});
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) {
        close();
        return;
      }
      timer = setTimeout(function () {
        fetchResults(q);
      }, 300);
    });

    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= 2 && cache[q]) show(q, cache[q]);
    });

    input.addEventListener('keydown', function (e) {
      var opts = options();
      if (e.key === 'ArrowDown') {
        if (panel.hidden) return;
        e.preventDefault();
        if (opts.length) setActive((active + 1) % opts.length);
      } else if (e.key === 'ArrowUp') {
        if (panel.hidden) return;
        e.preventDefault();
        if (opts.length) setActive(active <= 0 ? opts.length - 1 : active - 1);
      } else if (e.key === 'Enter') {
        if (!panel.hidden && active >= 0 && opts[active]) {
          e.preventDefault();
          window.location.href = opts[active].querySelector('a').href;
        }
      } else if (e.key === 'Escape') {
        if (!panel.hidden) {
          e.preventDefault();
          close();
        }
      }
    });

    form.addEventListener('submit', function (e) {
      if (input.value.trim() === '') e.preventDefault();
    });

    qsa(form, '[data-c-blog-list-suggest]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        input.value = a.dataset.cBlogListSuggest;
        input.focus();
        fetchResults(input.value);
      });
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
    form.addEventListener('focusout', function (e) {
      if (e.relatedTarget && !form.contains(e.relatedTarget)) close();
    });
  };

  /* ---------- Featured band slider dots ---------- */

  BlogListing.prototype.initBand = function () {
    var track = this.root.querySelector('[data-c-blog-list-band-track]');
    var dotsWrap = this.root.querySelector('[data-c-blog-list-band-dots]');
    if (!track || !dotsWrap) return;
    var slides = qsa(track, '.c-blog-list-band__slide');
    if (slides.length < 2) return;

    dotsWrap.innerHTML = slides
      .map(function (_, i) {
        return (
          '<button type="button" class="c-blog-list-band__dot" aria-label="Go to slide ' +
          (i + 1) +
          '" data-index="' +
          i +
          '"></button>'
        );
      })
      .join('');
    var dots = qsa(dotsWrap, '.c-blog-list-band__dot');

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var idx;
      if (track.scrollLeft >= max - 2) {
        idx = slides.length - 1;
      } else {
        var step = slides[1].offsetLeft - slides[0].offsetLeft || 1;
        idx = Math.round(track.scrollLeft / step);
      }
      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        var i = parseInt(d.dataset.index, 10);
        track.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    });

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );
    update();
  };

  function init() {
    qsa(document, '[data-c-blog-list]').forEach(function (root) {
      if (root._cBlogList) return;
      root._cBlogList = new BlogListing(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
